import React from 'react';
import { Action, withInject } from '@piwikpro/platform';
import {
  Stack,
  Icon,
  Tooltip,
  Text,
  Trans,
  withTranslation,
} from '@piwikpro/ui-components';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';

export interface KeepUrlFragmentProps {
  appId: string
  trackerSettings: TrackerSettings
  globalTrackerSettings: TrackerSettingsResponse
  appTrackerSettings: TrackerSettingsResponse
  t: (key: string) => string
}

export interface KeepUrlFragmentState {
  useGlobals: boolean
}

class KeepUrlFragment extends React.Component<
KeepUrlFragmentProps,
KeepUrlFragmentState
> {
  static readonly fieldName = 'keep_url_fragment';

  constructor(props) {
    super(props);
    const { appTrackerSettings } = props;

    this.state = {
      useGlobals: appTrackerSettings.attributes?.[KeepUrlFragment.fieldName] === undefined,
    };

    this.handleSubmitSwitcher = this.handleSubmitSwitcher.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);
  }

  async handleSubmitSwitcher(values, { setFieldValue, setSubmitting }) {
    const { trackerSettings, appId } = this.props;

    try {
      await trackerSettings.updateSettingsForApp(appId, values);
    } catch (err) {
      setFieldValue(KeepUrlFragment.fieldName, !values[KeepUrlFragment.fieldName], false);
    }

    setSubmitting(false);
  }

  async handleSubmitCheckbox(values, { setFieldValue, setSubmitting }) {
    const {
      trackerSettings,
      globalTrackerSettings,
      appId,
    } = this.props;

    try {
      if (values.keep_url_fragment_global) {
        await trackerSettings.deleteSettingsForApp(appId, KeepUrlFragment.fieldName);
      } else {
        await trackerSettings.updateSettingsForApp(appId, {
          [KeepUrlFragment.fieldName]:
            globalTrackerSettings.attributes[KeepUrlFragment.fieldName],
        });

        setFieldValue(
          KeepUrlFragment.fieldName,
          globalTrackerSettings.attributes[KeepUrlFragment.fieldName],
          false,
        );
      }
    } catch (err) {
      setFieldValue('keep_url_fragment_global', !values.keep_url_fragment_global, false);
      this.setState({ useGlobals: !values.keep_url_fragment_global });

      setSubmitting(false);

      return;
    }

    this.setState({
      useGlobals: values.keep_url_fragment_global,
    });

    setSubmitting(false);
  }

  render() {
    const {
      appTrackerSettings,
      globalTrackerSettings,
      t,
    } = this.props;
    const { useGlobals } = this.state;

    return (
      <Stack vertical>
        <Stack.Item fill>
          <Stack wrap verticalAlignment="center">
            <Stack.Item fill>
              <Stack vertical spacing="narrow">
                <Stack.Item>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                    {t('apps.details.settings.tracking.keep-page-url.title')}
                    <div style={{ display: 'inline-block', marginLeft: '8px' }}>
                      <Tooltip
                        content={(
                          <Trans
                            i18nKey="apps.details.settings.tracking.keep-page-url.tooltip"
                            components={[
                              <b>text</b>,
                            ]}
                          />
                        )}
                      >
                        <div><Icon name="info-circle" /></div>
                      </Tooltip>
                    </div>
                  </Text>
                </Stack.Item>
                <Stack.Item>
                  <Text as="p">{t('apps.details.settings.tracking.keep-page-url.description')}</Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Action.AutoSavedSwitcherControl
                id="app-settings-tracking-keep-url-fragment"
                initialValue={
                  useGlobals
                    ? globalTrackerSettings.attributes[KeepUrlFragment.fieldName]
                    : appTrackerSettings.attributes[KeepUrlFragment.fieldName]
                }
                label={t('apps.details.settings.tracking.keep-page-url.title')}
                info={t('apps.details.settings.tracking.keep-page-url.description')}
                name={KeepUrlFragment.fieldName}
                onSubmit={this.handleSubmitSwitcher}
                disabled={useGlobals}
                disabledActionTooltipText={t('app.details.settings.general.use-global-settings.block-reason')}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack spacing="narrow">
            <Stack.Item>
              <Action.AutoSavedCheckboxControl
                id="app-settings-keep-url-fragment-use-globals"
                onSubmit={this.handleSubmitCheckbox}
                initialValue={useGlobals}
                label={t('app.details.settings.general.use-global-settings.label')}
                name="keep_url_fragment_global"
              />
            </Stack.Item>
            <Stack.Item>
              <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                <div>
                  <Icon name="info-circle" />
                </div>
              </Tooltip>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(
  withTranslation(['administration'])(KeepUrlFragment as any),
);
